import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import LocationOn from '@mui/icons-material/LocationOn';
import Phone from '@mui/icons-material/Phone';
import { School, CrudPermissions } from '../../types';
import {
  getSchools,
  addSchool,
  updateSchool,
  deleteSchool,
} from '../../mock/schools';

interface SchoolsManagementProps {
  permissions: CrudPermissions;
}

export const SchoolsManagement: React.FC<SchoolsManagementProps> = ({ permissions }) => {
  const [schools, setSchools] = useState<School[]>(getSchools);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<School>>({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    isActive: true,
  });

  const refreshSchools = () => {
    setSchools(getSchools());
  };

  const filteredSchools = useMemo(() => {
    return schools.filter((school) => {
      const matchesSearch =
        school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (school.city && school.city.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesSearch;
    });
  }, [schools, searchQuery]);

  const handleAdd = () => {
    setEditingSchool(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      isActive: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = (school: School) => {
    setEditingSchool(school);
    setFormData({
      name: school.name,
      code: school.code,
      address: school.address || '',
      city: school.city || '',
      state: school.state || '',
      zipCode: school.zipCode || '',
      phone: school.phone || '',
      isActive: school.isActive,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this school? This may affect students and fees associated with it.')) {
      deleteSchool(id);
      refreshSchools();
    }
  };

  const handleSave = () => {
    if (editingSchool) {
      updateSchool(editingSchool.id, {
        name: formData.name!,
        code: formData.code!.toUpperCase(),
        address: formData.address || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
        phone: formData.phone || undefined,
        isActive: formData.isActive!,
      });
    } else {
      addSchool({
        name: formData.name!,
        code: formData.code!.toUpperCase(),
        address: formData.address || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
        phone: formData.phone || undefined,
        isActive: formData.isActive!,
      });
    }
    refreshSchools();
    setOpenDialog(false);
  };

  const formatAddress = (school: School) => {
    const parts = [];
    if (school.address) parts.push(school.address);
    if (school.city || school.state || school.zipCode) {
      const cityState = [school.city, school.state].filter(Boolean).join(', ');
      if (cityState && school.zipCode) {
        parts.push(`${cityState} ${school.zipCode}`);
      } else if (cityState) {
        parts.push(cityState);
      } else if (school.zipCode) {
        parts.push(school.zipCode);
      }
    }
    return parts.join(', ');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Schools Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage schools in your district
          </Typography>
        </Box>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add School
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search schools..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(auto-fill, minmax(320px, 1fr))',
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {filteredSchools.map((school) => (
          <Card
            key={school.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <SchoolIcon />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, fontSize: '1rem' }}>
                    {school.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={school.code}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                    <Chip
                      label={school.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={school.isActive ? 'success' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              </Box>
              {formatAddress(school) && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {formatAddress(school)}
                  </Typography>
                </Box>
              )}
              {school.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {school.phone}
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
              {permissions.update && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => handleEdit(school)}
                  sx={{ flexGrow: 1, minWidth: 0 }}
                >
                  Edit
                </Button>
              )}
              {permissions.delete && (
                <IconButton
                  size="small"
                  onClick={() => handleDelete(school.id)}
                  color="error"
                  sx={{ flexShrink: 0 }}
                >
                  <Delete />
                </IconButton>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      {filteredSchools.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No schools found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add School" to create your first school'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSchool ? 'Edit School' : 'Add New School'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="School Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Lincoln Elementary School"
            />
            <TextField
              label="School Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              fullWidth
              required
              placeholder="e.g., LES"
              helperText="Short code used for identification (will be uppercased)"
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              placeholder="e.g., 100 School Drive"
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                fullWidth
                placeholder="e.g., Springfield"
              />
              <TextField
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                fullWidth
                placeholder="e.g., IL"
                inputProps={{ maxLength: 2 }}
              />
              <TextField
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                fullWidth
                placeholder="e.g., 62701"
              />
            </Box>
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              placeholder="e.g., 555-0100"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.code}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
